import { useMutation, useQuery } from 'react-query';
import { requestXlsxFileDownload, request_v2 } from '../utils/axios-utils';
import { parseSkuSearchList } from '../utils/parseSkuSearchList';

function skuSearchRequest(searchText, params) {
    const { terms } = parseSkuSearchList(searchText || '');
    if (terms.length > 1) {
        return {
            method: 'post',
            params,
            body: { searchTexts: terms },
        };
    }
    return {
        method: 'get',
        params: { ...params, searchText: terms[0] || '' },
        body: null,
    };
}

const SearchSku = async (
    companyCode,
    orderId,
    searchText,
    pageNum,
    pageSize,
    isCCRequired,
    isDDApproved,
    isPendingAtRawQC,
    markedAsSupport,
    isOutbound,
    isSpecialCategory
) => {
    const params = {
        orderId,
        pageNum,
        pageSize,
        companyCode,
        isCCRequired,
        isDDApproved,
        isPendingAtRawQC,
        markedAsSupport,
        isOutbound,
    };
    if (isSpecialCategory) {
        params.isSpecialCategory = isSpecialCategory;
    }

    const request = skuSearchRequest(searchText, params);
    const { data } = await request_v2({
        method: request.method,
        url: '/sku/SearchSku2',
        params: request.params,
        body: request.body,
    });

    return data;
};
const GetCategories = async () => {
    const { data } = await request_v2({
        method: 'get',
        url: '/requests/listReshootCategories',
    });
    return data;
};
const GetSkuDetailsById = async skuId => {
    if (!skuId) {
        return {};
    }
    const { data } = await request_v2({
        method: 'get',
        url: `/sku/getSkuDetailsById`,
        params: { skuId: skuId },
    });
    return data;
};

const UpdateCCRequired = async (skuId, isCCRequired) => {
    if (!skuId) {
        return {};
    }
    const { data } = await request_v2({
        method: 'post',
        url: `/sku/updateCCRequired`,
        body: { skuId: skuId, isCCRequired: isCCRequired },
    });
    return data;
};

const RerunSkuFromShoot = async (skuId, comment, category, angleIds, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/newSkuRerunFromShoot',
        body: {
            skuId: skuId,
            comment: comment,
            category,
            angleIds,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};
const RerunSkuFromRawQC = async (skuId, comment, category, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/newSkuRerunFromRawQC',
        body: {
            skuId: skuId,
            comment: comment,
            category,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};
const addCategoryToList = async category => {
    return await request_v2({
        method: 'POST',
        url: '/requests/createReshootCategory',
        body: {
            category,
        },
    });
};

export const useSearchSku = (
    companyCode,
    orderId,
    searchText,
    pageNum,
    pageSize,
    isCCRequired,
    isDDApproved,
    isPendingAtRawQC,
    markedAsSupport,
    isOutbound,
    isSpecialCategory
) =>
    useQuery(
        [
            'skus',
            companyCode,
            orderId,
            searchText,
            pageNum,
            pageSize,
            isCCRequired,
            isDDApproved,
            isPendingAtRawQC,
            markedAsSupport,
            isOutbound,
            isSpecialCategory,
        ],
        () =>
            SearchSku(
                companyCode,
                orderId,
                searchText,
                pageNum,
                pageSize,
                isCCRequired,
                isDDApproved,
                isPendingAtRawQC,
                markedAsSupport,
                isOutbound,
                isSpecialCategory
            ),
        {
            refetchOnWindowFocus: false,
        }
    );

export const useGetCategoriesForReshoot = data =>
    useQuery(['fetchCategories', data], () => GetCategories(), {
        refetchOnWindowFocus: false,
    });

export const useRerunSkuFromShoot = () =>
    useMutation({
        mutationFn: async ([skuId, comment, category, angleIds, autoApprove]) => {
            return await RerunSkuFromShoot(skuId, comment, category, angleIds, autoApprove);
        },
    });

export const useRerunSkuFromRawQC = () =>
    useMutation({
        mutationFn: async ([skuId, comment, category, autoApprove]) => {
            return await RerunSkuFromRawQC(skuId, comment, category, autoApprove);
        },
    });
export const useGetSkuDetailsById = (skuId, isEnabled = true) =>
    useQuery(['skuDetails', skuId], () => GetSkuDetailsById(skuId), {
        // refetchOnWindowFocus: false,
        keepPreviousData: false,
        enabled: isEnabled,
    });

export const useUpdateCCRequired = () =>
    useMutation({
        mutationFn: async ([skuId, isCCRequired]) => {
            return await UpdateCCRequired(skuId, isCCRequired);
        },
    });
export const useAddCategoryToList = () =>
    useMutation({
        mutationFn: async ([category]) => {
            return await addCategoryToList(category);
        },
    });

const updateCsvData = body => {
    return request_v2({ url: `/sku/updateCsvData`, method: 'POST', body });
};

export const useUpdateCsvData = (onSuccess, onError) => {
    return useMutation(updateCsvData, {
        onSuccess,
        onError,
    });
};

const MarkAsSupportSku = async (skuId, comment, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/markAsSupport',
        body: {
            skuId: skuId,
            comment: comment,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};
const RaiseClientRequest = async (skuId, comment, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/clientClarification',
        body: {
            skuId: skuId,
            comment: comment,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};

const RaiseResetSkuForProductionRequest = async (skuId, comment, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/newSkuRerunToProduction',
        body: {
            skuId: skuId,
            comment: comment,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};

const RaiseRePIIRequest = async (skuId, comment, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/rePII',
        body: {
            skuId: skuId,
            comment: comment,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};

const AddHeroToFundus = async (skuId, comment, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/heroToFundusConversion',
        body: {
            skuId: skuId,
            comment: comment,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};
export const useMarkAsSupportSku = () =>
    useMutation({
        mutationFn: async ([skuId, comment, autoApprove]) => {
            return await MarkAsSupportSku(skuId, comment, autoApprove);
        },
    });

export const useClientRequest = () =>
    useMutation({
        mutationFn: async ([skuId, comment, autoApprove]) => {
            return await RaiseClientRequest(skuId, comment, autoApprove);
        },
    });

export const useResetForProductionRequest = () =>
    useMutation({
        mutationFn: async ([skuId, comment, autoApprove]) => {
            return await RaiseResetSkuForProductionRequest(skuId, comment, autoApprove);
        },
    });

export const useRePIIRequest = () =>
    useMutation({
        mutationFn: async ([skuId, comment, autoApprove]) => {
            return await RaiseRePIIRequest(skuId, comment, autoApprove);
        },
    });

export const useAddHeroToFundus = () =>
    useMutation({
        mutationFn: async ([skuId, comment, autoApprove]) => {
            return await AddHeroToFundus(skuId, comment, autoApprove);
        },
    });

const RaiseChangeSkuOrder = async (skuId, targetOrderId, comment) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/changeSkuOrder',
        body: {
            skuId,
            targetOrderId,
            comment,
        },
    });
    return data;
};

export const useChangeSkuOrderRequest = () =>
    useMutation({
        mutationFn: async ([skuId, targetOrderId, comment]) => {
            return await RaiseChangeSkuOrder(skuId, targetOrderId, comment);
        },
    });

const DownloadSkusRequestExcel = async data => {
    const { searchText, ...parentParams } = data;
    const request = skuSearchRequest(searchText, parentParams);

    return await requestXlsxFileDownload({
        method: request.method,
        url: '/sku/SearchSkuAndGenerateExcel',
        params: request.params,
        data: request.body || undefined,
    });
};

export const useDownloadExcelForSku = (onSuccess, onError) => {
    return useMutation(DownloadSkusRequestExcel, {
        onSuccess: onSuccess,
        onError: onError,
        // refetchOnWindowFocus: false,
    });
};

const RaisePresetChangeRequest = async (skuId, comment, reshootRequired, anglePresetId, model, autoApproveDD, autoApprove) => {
    const { data } = await request_v2({
        method: 'POST',
        url: '/requests/newPresetChangeRequest',
        body: {
            skuId: skuId,
            comment: comment,
            reshootRequired,
            anglePresetId,
            model,
            autoApproveDD,
            autoApprove: autoApprove === true,
        },
    });
    return data;
};
export const useRaisePresetChangeRequest = () =>
    useMutation({
        mutationFn: async ([skuId, comment, reshootRequired, anglePresetId, model, autoApproveDD, autoApprove]) => {
            return await RaisePresetChangeRequest(skuId, comment, reshootRequired, anglePresetId, model, autoApproveDD, autoApprove);
        },
    });
